import {
  Button as AriaButton,
  type ButtonProps as AriaButtonProps,
  composeRenderProps,
} from "react-aria-components";
import { tv } from "tailwind-variants";
import { focusButtonStyles } from "./primitive";

const buttonStyles = tv({
  extend: focusButtonStyles,
  base: [
    "inline-flex items-center justify-center gap-x-2 font-medium",
    "cursor-pointer rounded-lg border border-transparent text-sm",
    "forced-colors:[--button-icon:ButtonText] forced-colors:hover:[--button-icon:ButtonText]",
    "*:data-[slot=icon]:my-1 *:data-[slot=icon]:size-4 *:data-[slot=icon]:shrink-0 *:data-[slot=icon]:text-(--button-icon)",
  ],
  variants: {
    intent: {
      primary: [
        "bg-primary text-primary-fg hover:bg-primary/90 pressed:bg-primary/80",
        "[--button-icon:var(--color-primary-fg)]",
      ],
      secondary: [
        "bg-secondary text-secondary-fg hover:bg-secondary/80 pressed:bg-secondary/70",
        "[--button-icon:var(--color-secondary-fg)]",
      ],
      danger: [
        "bg-danger text-danger-fg hover:bg-danger/90 pressed:bg-danger/80",
        "[--button-icon:var(--color-danger-fg)]",
      ],
      outline: [
        "border-border text-fg hover:bg-secondary pressed:bg-secondary/80",
        "[--button-icon:var(--color-fg)]",
      ],
      plain: [
        "text-fg hover:bg-secondary pressed:bg-secondary/80",
        "[--button-icon:var(--color-fg)]",
      ],
    },
    size: {
      "extra-small": "h-8 px-2.5 text-xs *:data-[slot=icon]:size-3.5",
      small: "h-9 px-3 text-sm",
      medium: "h-10 px-4 text-sm",
      large: "h-11 px-5 text-base",
    },
    shape: {
      square: "size-10 shrink-0",
      circle: "size-10 shrink-0 rounded-full",
    },
    isDisabled: {
      true: "cursor-default opacity-50",
    },
    isPending: {
      true: "cursor-default opacity-75",
    },
  },
  defaultVariants: {
    intent: "primary",
    size: "medium",
  },
});

export interface ButtonProps extends AriaButtonProps {
  intent?: "primary" | "secondary" | "danger" | "outline" | "plain";
  size?: "extra-small" | "small" | "medium" | "large";
  shape?: "square" | "circle";
}

export function Button({ className, intent, size, shape, ...props }: ButtonProps) {
  return (
    <AriaButton
      className={composeRenderProps(className, (className, renderProps) =>
        buttonStyles({
          ...renderProps,
          intent,
          size,
          shape,
          className,
        }),
      )}
      {...props}
    />
  );
}
